import { useState, useCallback, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GetFolderDocumentsPaginatedDocument, Document } from '../../../../gql/graphql';

interface FolderDataState {
  loading: boolean;
  error: string | null;
  documents: Document[];
  hasMore: boolean;
  skip: number;
}

interface UseLazyFolderDataReturn {
  folderDataMap: Map<string, FolderDataState>;
  loadFolderDocuments: (folderId: string) => Promise<void>;
  prefetchFolderDocuments: (folderId: string) => void;
  isLoading: (folderId: string) => boolean;
  getDocuments: (folderId: string) => Document[];
  clearFolderData: (folderId: string) => void;
  removeDocument: (documentId: string) => void;
  removeDocuments: (documentIds: string[]) => void;
}

const PAGE_SIZE = 50; // Load 50 documents at a time

export const useLazyFolderData = (): UseLazyFolderDataReturn => {
  // Store folder data states in a Map for O(1) lookups
  const [folderDataMap, setFolderDataMap] = useState<Map<string, FolderDataState>>(new Map());

  // Track ongoing prefetch requests to avoid duplicates
  const prefetchCancelMap = useRef<Map<string, boolean>>(new Map());

  const [loadDocuments] = useLazyQuery(GetFolderDocumentsPaginatedDocument, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });

  // Load documents for a specific folder
  const loadFolderDocuments = useCallback(
    async (folderId: string) => {
      const currentState = folderDataMap.get(folderId);

      // Skip if already loading
      if (currentState?.loading) return;

      const skip = currentState?.skip || 0;

      // Update loading state
      setFolderDataMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(folderId, {
          loading: true,
          error: null,
          documents: currentState?.documents || [],
          hasMore: currentState?.hasMore ?? true,
          skip
        });
        return newMap;
      });

      try {
        const { data, error } = await loadDocuments({
          variables: {
            folderId,
            skip,
            take: PAGE_SIZE,
            searchQuery: '' // No search filter for initial load
          }
        });

        if (error) {
          throw new Error(error.message);
        }

        const newDocuments = (data?.findManyDocuments || []) as Document[];
        const hasMore = newDocuments.length === PAGE_SIZE;

        setFolderDataMap((prev) => {
          const newMap = new Map(prev);
          const existingDocs = currentState?.documents || [];

          // Merge documents, avoiding duplicates
          const docMap = new Map<string, Document>();
          [...existingDocs, ...newDocuments].forEach((doc) => {
            docMap.set(doc.id, doc);
          });

          newMap.set(folderId, {
            loading: false,
            error: null,
            documents: Array.from(docMap.values()),
            hasMore,
            skip: skip + PAGE_SIZE
          });
          return newMap;
        });
      } catch (err: any) {
        setFolderDataMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(folderId, {
            loading: false,
            error: err.message || 'Failed to load documents',
            documents: currentState?.documents || [],
            hasMore: false,
            skip
          });
          return newMap;
        });
      }
    },
    [folderDataMap, loadDocuments]
  );

  // Prefetch documents in background (no loading state shown)
  const prefetchFolderDocuments = useCallback(
    (folderId: string) => {
      const currentState = folderDataMap.get(folderId);

      // Skip if already loaded or loading
      if (currentState?.documents.length || currentState?.loading) return;

      // Skip if already prefetching
      if (prefetchCancelMap.current.get(folderId)) return;

      // Mark as prefetching
      prefetchCancelMap.current.set(folderId, true);

      // Prefetch silently (no loading state update)
      loadDocuments({
        variables: {
          folderId,
          skip: 0,
          take: PAGE_SIZE,
          searchQuery: ''
        }
      })
        .then(({ data }) => {
          const newDocuments = (data?.findManyDocuments || []) as Document[];
          const hasMore = newDocuments.length === PAGE_SIZE;

          setFolderDataMap((prev) => {
            const newMap = new Map(prev);
            newMap.set(folderId, {
              loading: false,
              error: null,
              documents: newDocuments,
              hasMore,
              skip: PAGE_SIZE
            });
            return newMap;
          });
        })
        .catch((err) => {
          console.error(`Prefetch failed for folder ${folderId}:`, err);
        })
        .finally(() => {
          // Clear prefetch flag
          prefetchCancelMap.current.delete(folderId);
        });
    },
    [folderDataMap, loadDocuments]
  );

  // Check if folder is currently loading
  const isLoading = useCallback((folderId: string) => folderDataMap.get(folderId)?.loading || false, [folderDataMap]);

  // Get documents for a folder
  const getDocuments = useCallback((folderId: string) => folderDataMap.get(folderId)?.documents || [], [folderDataMap]);

  // Clear cached data for a folder (useful after delete/update)
  const clearFolderData = useCallback((folderId: string) => {
    setFolderDataMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(folderId);
      return newMap;
    });
    prefetchCancelMap.current.delete(folderId);
  }, []);

  // Optimistically remove a single document from whichever folder it belongs to
  const removeDocument = useCallback((documentId: string) => {
    setFolderDataMap((prev) => {
      const newMap = new Map(prev);
      for (const [folderId, state] of newMap) {
        const filtered = state.documents.filter((d) => d.id !== documentId);
        if (filtered.length !== state.documents.length) {
          newMap.set(folderId, { ...state, documents: filtered });
          break;
        }
      }
      return newMap;
    });
  }, []);

  // Optimistically remove multiple documents from their respective folders
  const removeDocuments = useCallback((documentIds: string[]) => {
    const idSet = new Set(documentIds);
    setFolderDataMap((prev) => {
      const newMap = new Map(prev);
      for (const [folderId, state] of newMap) {
        const filtered = state.documents.filter((d) => !idSet.has(d.id));
        if (filtered.length !== state.documents.length) {
          newMap.set(folderId, { ...state, documents: filtered });
        }
      }
      return newMap;
    });
  }, []);

  return {
    folderDataMap,
    loadFolderDocuments,
    prefetchFolderDocuments,
    isLoading,
    getDocuments,
    clearFolderData,
    removeDocument,
    removeDocuments
  };
};
